import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { freshAdvance, heldAdvance, pinnedCaption, type HeldInput, type PinInput } from './pinnedAdvance';
import { liveRestTransition, type RestTransition } from './restTransition';
import type { DraftEntry } from './state/loggerDraftTypes';
import type { LoggerCursor } from './useLoggerCursor';
import type { UseRestTimer } from './useRestTimer';

// The rest IS the transition (MX-5 D8). Logging a set used to move the focus
// immediately and flash a "→ Set 3/4" pill for 1.4s while a rest banner counted
// down somewhere else on screen; the set you had just logged was already behind
// you. Now the focus STAYS on it for the whole rest — verifiable, correctable —
// and moves when the rest ends.
//
// So the advance becomes a two-step: the decision is made at log time, against
// the pre-dispatch draft (which is what makes it correct — see restTransition),
// and it is APPLIED after a short transition window (LG-29, Antenore's spec).
// #387: a held move is resolved again from its source row when it is applied
// (liveRestTransition), since sets can be added or removed while it waits.
// You look at the set you just closed for ~2.5s; then the focus moves to the
// NEXT set/exercise in the queue while the rest keeps running, so you see what
// is coming while the clock finishes. The transition fires on a timer, NOT at
// rest end.
//
// TM-18a — and the athlete may now LEAVE. Moving the cursor by hand no longer
// throws the queued transition away, it defers it: their tap wins at once, and
// the end of the countdown brings them back to where the training continues
// («vagare è temporaneo», Antenore). The one thing that can hold the return is
// a field with the keyboard — see `anyFieldFocused`.
//
// ⚠️ CALLED FROM THE LoggerScreen BODY, ABOVE THE <Modal> — never from inside
// useTimelineLogger, which runs within it. This is the LG-13 rule and it is
// load-bearing here, not tidiness: minimize flips the Modal's visible to false
// and RN unmounts its whole subtree. A transition held below that line is
// DESTROYED by a minimize, while the rest timer (already hoisted, for the same
// reason) keeps counting — so the countdown would end a minute later with
// nothing left to apply and the focus would simply never advance, silently and
// with no symptom to notice. The cursor lives up here for exactly this reason,
// so the thing that moves it lives beside it.
//
// Why `restArmed` is passed in rather than observed: a rest is not always
// armed. An exercise whose default is 0 means "no rest", and editing a past
// session never rests at all. Inferring that from the timer's state would mean
// waiting to see whether it starts, and a transition that silently never fires
// is exactly the class of bug this slice is most able to introduce. The caller
// knows; it says so.
//
// TM-12 — a rest arms after the LAST set too, and there the transition is
// `done`: nothing is queued, so the hand-over has nowhere to go and the card
// stays put rather than blanking under a running countdown.
// So this hook remembers WHAT armed the running rest and publishes the two
// answers separately (Antenore 2026-08-09).

/** How long the "no rest to sit through" caption stays up. Same beat the
 *  retired useFocusAdvanceCue used — long enough to read, short enough not to
 *  linger over the next set. */
const FLASH_CUE_MS = 1400;

/** The transition window (LG-29, Antenore: "2, 3 secondi"). The set just logged
 *  stays in front of you for this long; then the focus hands over to the next
 *  set/exercise while the rest keeps running. Kept below any real rest (an armed
 *  rest is the exercise default, never under a few seconds), so it fires before
 *  the count ends; if a rest somehow ends first, the rest-end path below applies
 *  instead. */
const TRANSITION_DELAY_MS = 2500;

// ONE state, not four flags (TM-18a, on Antenore's call). The lifecycle is a
// small machine and the four independent slots it used to be — pending / armed /
// overridden / resumeWaiting — had to be hand-synchronised at six call sites.
// One of them updated two of the three it owed and the review caught the third:
// that is the bug shape independent booleans produce, and a union simply cannot
// spell it. Every arm below carries the transition it is about, so "which
// transition" is never a separate question from "what are we doing with it".
type AdvanceState =
  /** Nothing owed. Also where a rest that resolved cleanly returns to. */
  | { kind: 'idle' }
  /** A rest is running and the hand-over has NOT happened yet: the athlete is
   *  still looking at the set they just closed.
   *
   *  `touched` is a LATCH for the caption only (LG-70): true once a field has
   *  taken the keyboard inside this window. ⛔ It never reaches the hand-over
   *  timer, which asks `focusedRef` for a live answer — that separation IS the
   *  fix, and folding the two back together is how the bug returns.
   *
   *  ⚠️ Its ORIGINAL reason is gone (LG-72, 2026-09-06). It was written because
   *  `anyFieldFocused` dipped to false for one commit when RN handed the
   *  keyboard over blur-A-then-focus-B; `useFieldFocus` now holds the signal up
   *  across that hand-over, so the dip no longer arrives. The latch is KEPT
   *  deliberately, and not as leftovers: whether Android delivers those two
   *  events inside one JS task is the question LG-72 could not settle from the
   *  bench, so this is the net under a grace that might be too short — and it
   *  is a WIDER rule than the dip anyway (any keyboard inside the window keeps
   *  the caption down for the rest of it, which is the behaviour we want). ⛔ If
   *  a device ever proves the grace sufficient, removing this is a decision,
   *  not tidying. */
  | { kind: 'scheduled'; transition: RestTransition; touched: boolean }
  /** The hand-over fired on its window; the rest is still counting. Nothing is
   *  owed unless the athlete now moves by hand. */
  | { kind: 'applied'; transition: RestTransition }
  /** The athlete moved the cursor by hand. Their choice holds for the rest of
   *  the countdown, and the end of it returns them here. */
  | { kind: 'deferred'; transition: RestTransition }
  /** The rest ended owing a move, but a field held the keyboard. Released by
   *  the blur, never by the clock. */
  | { kind: 'resumeWaiting'; transition: RestTransition };

/** What the RUNNING rest is about, whether or not it has been applied yet.
 *  `resumeWaiting` is deliberately absent: by then the rest is over. */
function transitionOfRunningRest(state: AdvanceState): RestTransition | null {
  return state.kind === 'scheduled' ||
    state.kind === 'applied' ||
    state.kind === 'deferred'
    ? state.transition
    : null;
}

export function useRestAdvance({
  restTimer,
  cursor,
  anyFieldFocused = false,
  entries,
  pin,
}: {
  /** Only the countdown's STATE is read here. Narrowed on purpose: it is the
   *  whole of what this hook needs, and a wider type only buys test doubles
   *  that have to be cast — and a cast double is a contract switched off. */
  restTimer: Pick<UseRestTimer, 'state'>;
  /** The focus cursor, which this hook is the sole post-log mover of. */
  cursor: LoggerCursor;
  /** Whether a logger field currently holds the keyboard (useFieldFocus). The
   *  rest-end resume waits for it to let go: pulling the caret out of a
   *  half-typed weight is the bug we would be buying with the freeze gone. */
  anyFieldFocused?: boolean;
  /** #387: the live draft a held move is resolved again on when it applies.
   *  Absent ⇒ the pin's entries, and with neither the stored move stands. */
  entries?: readonly DraftEntry[];
  /** #308: the pinned row and the entries it is read against. A transition is
   *  redirected to the pin when it is APPLIED, so a pin set mid-rest counts. */
  pin?: PinInput;
}) {
  const [state, setState] = useState<AdvanceState>({ kind: 'idle' });
  // The draft and the pin, read when a move is APPLIED (write-through, as below).
  const draft = entries ?? pin?.entries;
  const live: HeldInput | undefined = draft ? { entries: draft, id: pin?.id ?? null } : undefined;
  const liveRef = useRef(live);
  liveRef.current = live;
  // TM-18b — taking the keyboard IS a manual move, so it defers the hand-over.
  // With the freeze gone the ~2.5s window can fire while a field holds the
  // caret: the panel rebinds to the next pending set, React keeps the same
  // TextInput node and the keyboard with it, so the typing lands in the NEXT
  // set in silence. That is the LG-26 loss, which the freeze used to make
  // unreachable by dismissing the keyboard outright. The rule is the one
  // already approved for a tap (§4, "his tap wins now"), and the rest END
  // brings the transition back.
  //
  // ⚠️ LG-70 moved WHERE that rule is enforced, not what it says. It used to be
  // an effect firing `defer()` the instant a field took the keyboard during a
  // rest — and `defer()` kills the hand-over timer, so ONE stale read parked
  // the transition for the whole countdown. It is asked inside the timer's own
  // callback now, at the only moment the answer changes anything; this ref is
  // how that callback sees a fresh answer without taking `anyFieldFocused` as a
  // dependency, which would re-arm the timer on every focus change and restart
  // the window. Write-through in the render body: the same idiom as `stateRef`
  // below and `useSelectAllOnFocus`'s length ref.
  const focusedRef = useRef(anyFieldFocused);
  focusedRef.current = anyFieldFocused;
  // Read inside the rest-end effect, which must see the LATEST state without
  // taking it as a dependency: that effect fires on the countdown ENDING, once,
  // and a state dependency would re-run it mid-count. Written in the render
  // body, so it is a read-through of the same value and can never drift from it
  // (same idiom as useSelectAllOnFocus's length ref).
  const stateRef = useRef<AdvanceState>(state);
  stateRef.current = state;

  // The caption for an advance that had NO rest behind it (a zero-rest lift).
  // Without it that advance would be signalled by the card's colour flash
  // alone, and colour alone is never allowed to be the only cue — the retired
  // cue hook always paired the flash with text, and dropping the text on this
  // path would have been a quiet a11y regression.
  const [flashCue, setFlashCue] = useState<RestTransition | null>(null);
  // Bumped every time a transition is APPLIED, so the FocusPanel's advance
  // flash still fires on the moment the card actually changes. It used to ride
  // on the retired cue hook's key, which counted the caption instead.
  const [advanceKey, setAdvanceKey] = useState(0);

  // Applies a transition — the ONLY place the cursor moves after a log.
  //
  // 'done' moves nothing, and that is TM-12a (Antenore, smoke of 4.14.2-dev,
  // 2026-08-09). It used to blank the focus and re-open the roster — "point A",
  // his own earlier pick, taken when `done` meant nothing more would happen on
  // screen. TM-12 changed that premise: a rest now runs there, the rest row
  // lives ON the focused card, so blanking the card HID a countdown that was
  // still ticking. His report: «fa il fade per mostrare l'esercizio successivo,
  // ma non c'è… non vedo più il timer, che è in esecuzione però, infatti se
  // tappo sulla card lo vedo.» ⇒ with nothing queued, the focus stays on the
  // exercise just closed.
  const { setActiveId, setSelectedSetIdx, setRosterExpanded } = cursor;
  // `fresh` is the no-rest path, applied in the same tap that logged the set,
  // where what this hook sees is one render old (freshAdvance says how the pin
  // is read then). Every other path is a held move (heldAdvance).
  const apply = useCallback(
    (owed: RestTransition, fresh = false): RestTransition => {
      const transition = fresh
        ? freshAdvance(owed, liveRef.current)
        : heldAdvance(owed, liveRef.current);
      if (transition.kind === 'exercise') {
        setActiveId(transition.entryId);
        setSelectedSetIdx(null);
        setRosterExpanded(false);
      } else if (transition.kind === 'set') {
        // Name the row as well as clear the selection (TM-18a). Clearing alone
        // was enough while the logger froze — you could not be anywhere else —
        // but a resume after the athlete wandered has to come BACK to the
        // exercise the rest belongs to, and a bare re-bind would leave the
        // cursor wherever they had gone. A no-op when they never left.
        setActiveId(transition.entryId);
        setSelectedSetIdx(null);
      } else {
        setSelectedSetIdx(null);
      }
      setAdvanceKey((k) => k + 1);
      return transition;
    },
    [setActiveId, setSelectedSetIdx, setRosterExpanded],
  );

  const flashTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  // The LG-29 transition timer: fires TRANSITION_DELAY_MS after a log to hand
  // the focus to the next set/exercise, mid-rest. Held in a ref so re-arming or
  // an early rest-end can cancel it before it double-applies.
  const transitionTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const clearTransitionTimer = useCallback(() => {
    if (transitionTimer.current) {
      clearTimeout(transitionTimer.current);
      transitionTimer.current = null;
    }
  }, []);
  useEffect(
    () => () => {
      if (flashTimer.current) clearTimeout(flashTimer.current);
      if (transitionTimer.current) clearTimeout(transitionTimer.current);
    },
    [],
  );

  const arm = useCallback(
    (transition: RestTransition, restArmed: boolean) => {
      clearTransitionTimer();
      if (!restArmed) {
        // Nothing to sit through: move now, and say so in words as well as in
        // the flash. 'done' carries no caption (there is nowhere to point) and,
        // since TM-12a, moves nothing either — it just stays put.
        //
        // Back to idle, which also drops anything the previous rest still owed:
        // a new logged set is a newer answer to "where should the focus be"
        // than a resume waiting on a blur, and leaving that resume alive would
        // jump the cursor a second time once the field let go.
        setState({ kind: 'idle' });
        const applied = apply(transition, true);
        if (applied.kind !== 'done') {
          setFlashCue(applied);
          if (flashTimer.current) clearTimeout(flashTimer.current);
          flashTimer.current = setTimeout(() => setFlashCue(null), FLASH_CUE_MS);
        }
        return;
      }
      // A rest is running. Hold the transition for the caption and arm the timer
      // that hands the focus over after the ~2.5s window (LG-29). The captured
      // `transition` is applied directly; the rest-end path below clears this
      // timer if the count somehow finishes first, so it never applies twice.
      // Unconditional, like the branch above and for the same reason: a set
      // just logged is a newer answer than anything the previous rest still
      // owed, resumeWaiting included.
      setState({ kind: 'scheduled', transition, touched: false });
      transitionTimer.current = setTimeout(() => {
        transitionTimer.current = null;
        // LG-70 — the keyboard is asked HERE, at the one moment it matters,
        // and not the instant a field takes it. The difference is the whole
        // bug: `dismissSoftKeyboard` (LG-69) runs AFTER onLogSet, so in the
        // commit where the rest starts a field the athlete has just finished
        // typing into still reads focused, and the blur only lands a native
        // round-trip later. Deferring on that read parked every hand-over
        // that followed a typed weight until the rest ENDED — 90-180s instead
        // of 2.5s, which from the athlete's chair is "it doesn't advance".
        // Two and a half seconds later that stale read is long gone, and a
        // field still holding the keyboard here really is someone typing.
        if (focusedRef.current) {
          // Genuinely mid-correction (TM-18b, LG-26): the panel must not
          // rebind under the caret, so the hand-over waits for the rest to
          // end exactly as a manual move does.
          setState({ kind: 'deferred', transition });
          return;
        }
        apply(transition);
        setState({ kind: 'applied', transition });
      }, TRANSITION_DELAY_MS);
    },
    [apply, clearTransitionTimer],
  );

  // The athlete moved the cursor by hand. Their tap wins NOW: the queued
  // hand-over is dropped and nothing relocates the screen under them.
  //
  // It is a DEFERRAL, not a cancellation (TM-18a, and the reason this is no
  // longer called `cancel`). The transition is remembered, and the end of the
  // countdown brings the focus back to it. A resume already waiting on a field
  // is dropped though: the rest is over, so there is no later moment to return
  // them, and their choice is the last word.
  const defer = useCallback(() => {
    clearTransitionTimer();
    setState((current) => {
      const transition = transitionOfRunningRest(current);
      if (transition) {
        // Same object when the deferral is already in place: focus moving
        // between two fields raises this again, and a fresh state each time
        // would re-render the logger mid-typing.
        return current.kind === 'deferred' && current.transition === transition
          ? current
          : { kind: 'deferred', transition };
      }
      // Nothing queued: hand BACK the same object so React bails out. The
      // focus deferral below fires on every field that takes the keyboard,
      // and a fresh { kind: 'idle' } each time would re-render the logger
      // for nothing.
      return current.kind === 'idle' ? current : { kind: 'idle' };
    });
  }, [clearTransitionTimer]);

  // A rest is over when the countdown reaches 0 (`finished`, the moment the cue
  // fires — NOT the end of the 3s grace window, which only ever existed to let
  // the old banner fade out) or when it is cleared, which is the ✕ on the
  // meter's column and the reset that re-arming performs. Both endings owe the
  // same thing: this hook has never distinguished "finished" from "skipped",
  // and TM-18a keeps that — a ✕ means "done resting", so it returns you to the
  // work exactly as running out does.
  const { endsAt, isPaused, finished } = restTimer.state;
  const resting = (endsAt !== null && !finished) || isPaused;
  const wasResting = useRef(false);
  useEffect(() => {
    const was = wasResting.current;
    wasResting.current = resting;
    if (!was || resting) return;
    // Two things can still be owed here:
    //   - a hand-over that never fired — a rest shorter than the ~2.5s window,
    //     or a skip inside it, ends before the timer does;
    //   - a RESUME, because the athlete moved by hand and the transition was
    //     deferred to this moment.
    // Cancel the pending timer first so nothing double-applies.
    clearTransitionTimer();
    const current = stateRef.current;
    const owed =
      current.kind === 'scheduled' || current.kind === 'deferred'
        ? current.transition
        : null;
    if (owed === null) {
      setState({ kind: 'idle' });
      return;
    }
    // ⚠️ Not while they are typing: the resume waits for the field to let go
    // rather than tearing the caret out of a half-typed number.
    if (anyFieldFocused) {
      setState({ kind: 'resumeWaiting', transition: owed });
      return;
    }
    apply(owed);
    setState({ kind: 'idle' });
  }, [resting, apply, clearTransitionTimer, anyFieldFocused]);

  // The resume the rest end could not perform, released by the blur.
  useEffect(() => {
    if (state.kind !== 'resumeWaiting' || anyFieldFocused) return;
    apply(state.transition);
    setState({ kind: 'idle' });
  }, [state, anyFieldFocused, apply]);

  // The caption latch (LG-70). ⛔ Deliberately NOT the eager effect this
  // replaced: that one called `defer()`, which clears the hand-over timer, and
  // one stale focus read then parked the transition for the whole countdown.
  // This only records that a correction has begun, so the caption stays down
  // for the rest of the window instead of blinking on a keyboard hand-over
  // between two cells.
  useEffect(() => {
    if (!anyFieldFocused) return;
    setState((current) =>
      current.kind === 'scheduled' && !current.touched
        ? { ...current, touched: true }
        : current,
    );
  }, [anyFieldFocused]);

  // ONE visibility rule (LG-70) for the plain caption and the pin's alike: the
  // pin changes the text, never whether the caption shows (#308).
  const captionShown =
    state.kind === 'scheduled' && !state.touched && !anyFieldFocused;
  // #387: the caption names the move the hand-over will make, so it reads the
  // held transition on the live draft too.
  const captioned = captionShown ? state.transition : null;
  const liveCaption = useMemo(
    () => (captioned && draft ? liveRestTransition(captioned, draft) : captioned),
    [captioned, draft],
  );

  return {
    /** The transition being waited out, for the rest row's caption. Null when
     *  no rest is running, when the rest was started by hand from the header (a
     *  manual rest transitions to nothing), once the hand-over has fired, and
     *  after a `defer` — the caption names what you are waiting FOR, and an
     *  athlete who has walked off to fix a weight is not waiting for it.
     *
     *  ⭐ The keyboard clears it too, and DERIVED rather than through the state
     *  machine (LG-70). Dropping the caption the moment a field takes over is a
     *  separate job from deciding whether the focus may move, and the two were
     *  tangled in one effect: honouring the first through `defer()` is what
     *  paid for it with the second. Here it costs nothing and is exact — the
     *  caption is gone in the same commit as the focus, with no state to
     *  unwind if they let go again a second later. */
    pending: pinnedCaption(
      liveCaption,
      liveCaption,
      pin?.id ?? null,
      draft ?? [],
    ),
    /** A just-applied transition with no rest behind it, for the transient
     *  caption on the card. Clears itself after a beat. */
    flashCue,
    /** A rest is counting — the condition for showing the rest bar. The bar is
     *  docked to the screen, not to a card, so it no longer rides the focus at
     *  all; the transition still owns where the focus goes, and a focus the
     *  athlete picked by hand is honoured until the countdown ends (`defer`). */
    resting,
    arm,
    defer,
    advanceKey,
  };
}
